import { Stack } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { first } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { FormSideDrawer } from '@/modules/common/SideDrawer/FormSideDrawer';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AugmentedCreateCashflowInput,
  AugmentedUpdateCashflowInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { CashflowDrawerBody } from './CashflowDrawer.components';
import {
  CashflowDrawerDefaultValues,
  CashflowDrawerFormShape,
} from './CashflowDrawer.types';
import {
  mapDataToCashflowDrawer,
  mapFormDataToCreatePayload,
  mapFormDataToUpdatePayload,
} from './CashflowDrawer.utils';
import {
  useCashflowQuery,
  useCreateCashflowMutation,
  useDeleteCashflowMutation,
  useUpdateCashflowMutation,
} from './graphql/Cashflows.generated';

export interface CashflowDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cashflowId: string | null;
  recipient?: string;
}

export function CashflowDrawer({
  cashflowId,
  isOpen,
  onClose,
  recipient,
}: CashflowDrawerProps) {
  const canEditCashflows = useCanEditHouseholdData();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { householdId } = useHouseholdDetailsContext();
  const trackUserEvent = useTrackUserEvent();
  const formMethods = useForm<CashflowDrawerFormShape>({
    defaultValues: {
      ...CashflowDrawerDefaultValues,
      targetId: recipient || CashflowDrawerDefaultValues.targetId,
    },
  });

  // The theme stacks the drawer layer above the modal layer, so a modal opened
  // from inside the drawer (the Entity field's add-entity flow) would render
  // behind it. Raise the modal layer above the drawer for the body subtree so
  // those modals — which derive their z-index from the theme and inherit it
  // through their portals — stack on top.
  const theme = useTheme();
  const elevatedModalTheme = useMemo(
    () => ({
      ...theme,
      zIndex: { ...theme.zIndex, modal: theme.zIndex.drawer + 1 },
    }),
    [theme]
  );

  const { handleSubmit, formState, shouldBlockNavigation, reset } = formMethods;
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This cash flow is no longer available.',
    onNotFound: onClose,
  });
  const { data, loading } = useCashflowQuery({
    variables: {
      where: {
        id: cashflowId,
      },
    },
    skip: !cashflowId,
  });

  const cashflow = first(getNodes(data?.cashflows));
  useEffect(() => {
    if (cashflow) {
      reset(mapDataToCashflowDrawer(cashflow));
    } else if (data && cashflowId) {
      recoverMissingRecord();
    }
  }, [cashflow, cashflowId, data, recoverMissingRecord, reset]);

  const [createCashflow, { loading: isCreatingCashflow }] =
    useCreateCashflowMutation({
      onError: (error) => {
        showFeedback(
          'Could not create the cash flow.  Please try again later.'
        );
        reportError('Caught error while creating cashflow', error);
      },
      awaitRefetchQueries: true,
      refetchQueries: 'active',
    });

  const [updateCashflow, { loading: isUpdatingCashflow }] =
    useUpdateCashflowMutation({
      onError: (error) => {
        showFeedback(
          'Could not update the cash flow.  Please try again later.'
        );
        reportError(
          `Caught error while updating cashflow ${cashflowId}`,
          error
        );
      },
      awaitRefetchQueries: true,
      refetchQueries: 'active',
    });

  const [deleteCashflow, { loading: isDeletingCashflow }] =
    useDeleteCashflowMutation({
      onError: (error) => {
        showFeedback(
          'Could not delete the cash flow.  Please try again later.'
        );
        reportError(
          `Caught error while deleting cashflow ${cashflowId}`,
          error
        );
      },
      awaitRefetchQueries: true,
      refetchQueries: 'active',
    });

  const onSubmit = handleSubmit(async (formData): Promise<void> => {
    if (cashflowId) {
      let payload: AugmentedUpdateCashflowInput;
      try {
        payload = mapFormDataToUpdatePayload(formData, cashflowId);
      } catch (error) {
        showFeedback(
          'Could not update the cash flow.  Please try again later.'
        );
        reportError(
          `Caught error while generating update cashflow ${cashflowId} payload`,
          error as Error
        );
        return;
      }

      const { data } = await updateCashflow({ variables: { input: payload } });

      if (!data?.updateCashflow.id) {
        // warn when the call succeeds but no data is returned
        diagnostics.warn(
          `No data returned for update cash flow ID [${cashflowId}]`,
          { payload }
        );
      }

      showFeedback('Cash flow updated successfully.', { variant: 'success' });
      reset({ ...CashflowDrawerDefaultValues });
      onClose();
    } else {
      let payload: AugmentedCreateCashflowInput;

      try {
        payload = mapFormDataToCreatePayload(formData, householdId);
      } catch (error) {
        showFeedback(
          'Could not create the cash flow.  Please try again later.'
        );
        reportError(
          'Caught error while generating create cashflow payload',
          error as Error
        );
        return;
      }
      const { data } = await createCashflow({ variables: { input: payload } });

      trackUserEvent('cash_flow created');

      if (!data?.createCashflow.id) {
        // warn when the call succeeds but no data is returned
        diagnostics.warn('No data returned for create cash flow', {
          payload,
        });
      }

      showFeedback('Cash flow created successfully.', { variant: 'success' });
      reset({ ...CashflowDrawerDefaultValues });
      onClose();
    }
  });

  const onCancel = useCallback(() => {
    onClose();
    reset({ ...CashflowDrawerDefaultValues });
  }, [onClose, reset]);

  const onDelete = useCallback(async () => {
    if (!cashflowId) {
      return;
    }
    await deleteCashflow({
      variables: { cashflowId },
      update: (cache) => {
        cache.evict({ id: `Cashflow:${cashflowId}` });
      },
    });
    reset({ ...CashflowDrawerDefaultValues });
    onClose();
  }, [cashflowId, deleteCashflow, onClose, reset]);

  const shouldBlock =
    isCreatingCashflow || isUpdatingCashflow || isDeletingCashflow;

  const actions = (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={1}
      width="100%"
    >
      <FormModalActions.Provider
        viewOnly={!canEditCashflows}
        formState={formState}
        disabled={shouldBlock}
      >
        {cashflowId && (
          <FormModalActions.DeleteButton onConfirmDelete={onDelete} />
        )}
        <FormModalActions.CancelButton onClick={onCancel} />
        <FormModalActions.SaveButton onClick={onSubmit}>
          {!!cashflowId ? 'Save changes' : 'Add cash flow'}
        </FormModalActions.SaveButton>
      </FormModalActions.Provider>
    </Stack>
  );

  const shouldBlockClose = shouldBlock || shouldBlockNavigation;

  return (
    <FormProvider {...formMethods}>
      <HouseholdPermissionAwareDisabledProvider
        forceDisabled={formState.isSubmitting || loading}
      >
        <FormSideDrawer
          isOpen={isOpen}
          onClose={onClose}
          heading={`${cashflowId ? 'Edit' : 'Add a'} cash flow`}
          shouldBlockClose={shouldBlockClose}
          bodyPropsSx={{ pt: 1.5 }}
          body={
            <ThemeProvider theme={elevatedModalTheme}>
              <CashflowDrawerBody
                householdId={householdId}
                recipient={recipient}
              />
            </ThemeProvider>
          }
          footer={actions}
        />
      </HouseholdPermissionAwareDisabledProvider>
    </FormProvider>
  );
}
